import { CarouselProductAttribute, ProductCarouselSlide } from '@dfh-corp-sites/ui'
import React from 'react'

import Link from '../../components/global/Link'
import { ParentProductFragment } from '../../graphql/search'
import { formatAsCurrency } from '../../utils/priceTools'
import { AnalyticsEventAddToCart } from '../analytics/events/recommended'
import { tryParsingSanityImageJson } from '../sanity'
import { productCardImageSourceProps } from './card'
import { openQuickViewProductModal } from './quick-view-modal'

/**
 * Transform variantAttributes from comma to shape expected by carousel component
 */
export const transformVariantAttributesForCarousel = (
  variantAttributes: NonNullable<ParentProductFragment['variantAttributes']>,
): Record<CarouselProductAttribute, string[]> =>
  variantAttributes.reduce(
    (acc: Record<CarouselProductAttribute, string[]>, attr) => {
      const attrName = attr.attribute.toLowerCase()
      if (attrName === 'size') acc.size = attr.values
      else if (attrName === 'flavor') acc.flavor = attr.values
      else if (attrName === 'dosage') acc.dosage = attr.values
      else if (attrName === 'flavor_size') acc.flavorSize = attr.values
      return acc
    },
    { size: [], flavor: [], dosage: [], flavorSize: [] },
  )

export const buildCarouselSlideProductData = ({
  analyticsEventAppContext,
  product,
  showPrice,
  showRetailPrice,
}: {
  analyticsEventAppContext: AnalyticsEventAddToCart['app_context']
  product: ParentProductFragment
  showPrice: boolean
  showRetailPrice: boolean
}): NonNullable<ProductCarouselSlide['productData']> => {
  const productPrice =
    product.priceRange?.minimum &&
    formatAsCurrency({ amount: parseFloat(product.priceRange.minimum) })

  const productRetailPrice =
    showRetailPrice && product.retailPriceRange?.minimum != null
      ? formatAsCurrency({ amount: parseFloat(product.retailPriceRange.minimum) })
      : undefined

  const sanityImageSource = product.mainImage?.json
    ? tryParsingSanityImageJson(product.mainImage.json)
    : undefined

  return {
    title: React.createElement(
      Link,
      {
        to: `/products/${product.urlKey}/`,
      },
      product.name,
    ),
    tag: product.advisorTags?.[0],
    price: productPrice,
    retailPrice: productRetailPrice,
    image: {
      ...productCardImageSourceProps(sanityImageSource),
      loading: 'lazy',
      alt: product.name || 'Product image',
    },
    attributes: transformVariantAttributesForCarousel(product.variantAttributes),
    showPrice,
    onAddToCart: () =>
      openQuickViewProductModal({
        analyticsEventAppContext,
        sku: product.sku,
        urlKey: product.urlKey,
      }),
  }
}
